/* Polyfills
// IE All
if (typeof String.prototype.endsWith !== 'function') {
  String.prototype.endsWith = function(suffix) {
    return this.indexOf(suffix, this.length - suffix.length) !== -1;
  };
}
// IE 9- | Chrome 34-
if (!Element.prototype.matches) Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
// IE All | Edge All | Chrome 41- | Firefox 35- | Safari 9-
if (!Element.prototype.closest)
  Element.prototype.closest = function(s) {
    let el = this;
    if (!document.documentElement.contains(el)) return null;
    do {
      if (el.matches(s)) return el;
      el = el.parentElement || el.parentNode;
    } while (el !== null && el.nodeType == 1);
    return null;
  }; */

export default class FormValidator {
  /**
   * Form validator
   * Validates form & fields based on html5 semantic
   *
   * @param {HTMLElement} form Form element
   * @property {Boolean} isValid Validation status of the whole form
   * @property {Array} errors Validation errors
   */
  constructor(form) {
    this.form = form;
    this.query = 'input:not([type=submit]):not([type=reset]):not([type=hidden]), select, textarea';
    this.fields = this.form.querySelectorAll(this.query);
    this.isValid = false;
    this.errors = [];
    this.requiredText = this.form.hasAttribute('data-txt-required')
      ? this.form.getAttribute('data-txt-required')
      : 'requis';
    this.notValidText = this.form.hasAttribute('data-txt-not-valid')
      ? this.form.getAttribute('data-txt-not-valid')
      : 'non valide';
    this.tooBigText = this.form.hasAttribute('data-txt-too-big')
      ? this.form.getAttribute('data-txt-too-big')
      : 'trop volumineux';
    this.initialize(form);
  }

  /**
   * Bind events & validate once
   */
  initialize() {
    for (const field of this.fields) {
      field.addEventListener('blur', this.validateField.bind(this));
      field.addEventListener('change', this.validateField.bind(this));
      field.addEventListener('keyup', this.validateField.bind(this));
      if (
        field.getAttribute('type') !== 'radio' &&
        field.getAttribute('type') !== 'checkbox' &&
        field.value.trim().length > 0
      ) {
        this.validateField(field);
      }
    }
    this.form.addEventListener('submit', this.validateForm.bind(this));
  }

  /**
   * Unbind events
   */
  destroy() {
    for (const field of this.fields) {
      field.removeEventListener('blur', this.validateField);
      field.removeEventListener('change', this.validateField);
      field.removeEventListener('keyup', this.validateField);
    }
    this.form.removeEventListener('submit', this.validateForm);
  }

  /**
   * Validate the whole form
   * @param {Event} evt
   * @returns {Boolean}
   */
  validateForm(evt) {
    const isValid = this.validateFields(this.fields);
    if (isValid) {
      return true;
    } else {
      if (evt !== undefined) evt.preventDefault();
      return false;
    }
  }

  /**
   * Validate one field
   * @param {Event|HTMLElement} elt
   * @returns {String} Error message
   */
  validateField(elt) {
    elt = elt.currentTarget || elt;
    let checked = false;
    let parent = null;
    const name = elt.getAttribute('name');
    switch (elt.getAttribute('type')) {
      case 'radio':
        parent = elt.closest('.form-radio-group');
        /** Validate the dependencies if the radio is checked */
        if (elt.checked) {
          this.validateDependencies(elt);
        }
        /** Check if one radio button is checked within the radio buttons group */
        if (parent.querySelector(`input[name='${name}'][value='${elt.value}']`) === elt) {
          for (const radio of parent.querySelectorAll(`input[name='${name}']`)) {
            if (radio.checked) {
              checked = true;
              break;
            }
          }
          if (checked) {
            this.setValid(parent);
          } else {
            this.setInvalid(parent);
            return `${this.getFieldLabel(elt)} ${this.requiredText}`;
          }
        }
        break;
      case 'checkbox':
        parent = elt.closest('.form-checkbox-group');
        if (elt.checked) {
          this.setValid(parent);
          this.validateDependencies(elt);
        } else {
          this.setInvalid(parent);
          return `${this.getFieldLabel(elt)} ${this.requiredText}`;
        }
        break;
      default:
        if (this.validateInputsByType(elt)) {
          this.setValid(elt);
          // this.validateDependencies(elt);
        } else {
          if (elt.getAttribute('type') === 'file') {
            if (elt.hasAttribute('data-maxsize')) {
              this.setInvalid(elt);
              return `${this.getFieldLabel(elt)} ${this.tooBigText}`;
            }
            if (elt.hasAttribute('required')) {
              parent = elt.closest('.custom-input-file-group');
              if (parent !== null) {
                const imageName = parent.querySelector('.image-name') && parent.querySelector('.image-name').value;
                const deleteImage =
                  parent.querySelector('.delete-image') && parent.querySelector('.delete-image').checked;
                if (imageName !== null && imageName.length > 0 && !deleteImage) {
                  this.setValid(elt);
                } else {
                  this.setInvalid(elt);
                  return `${this.getFieldLabel(elt)} ${this.requiredText}`;
                }
              }
            }
          } else {
            if (elt.hasAttribute('required')) {
              this.setInvalid(elt);
              return `${this.getFieldLabel(elt)} ${this.notValidText}`;
            } else if (elt.value.trim().length === 0) {
              this.setDefault(elt);
            }
          }
        }
        break;
    }
  }

  /**
   * Validate a group of fields
   * @param {Array} fields
   * @returns {Boolean}
   */
  validateFields(fields) {
    this.errors = [];
    for (const field of fields) {
      const res = this.validateField(field);
      if (res !== undefined) this.errors.push(res);
    }
    if (this.errors.length > 0) {
      this.isValid = false;
      return false;
    } else {
      this.isValid = true;
      return true;
    }
  }

  /**
   * Valdiate one field's dependencies
   * @param {HTMLElement} elt
   */
  validateDependencies(elt) {
    if (elt.hasAttribute('data-required') || elt.hasAttribute('data-not-required')) {
      let relatedField = null;
      if (elt.hasAttribute('data-required')) {
        relatedField = document.querySelector(`[name='${elt.getAttribute('data-required')}']`);
        relatedField.required = true;
      }
      if (elt.hasAttribute('data-not-required')) {
        relatedField = document.querySelector(`[name='${elt.getAttribute('data-not-required')}']`);
        relatedField.required = false;
      }
      this.validateField(relatedField);
      if (relatedField.required === false && relatedField.value.trim().length === 0) {
        this.setValid(relatedField);
      }
    }
  }

  /**
   * Get the text content of an element
   * @param {HTMLElement} elt
   * @returns {String}
   */
  getElementText(elt) {
    if (elt !== null) {
      const html = elt.innerHTML.trim().replace(/(<([^>]+)>)/gi, '');
      const text = document.createElement('textarea');
      text.innerHTML = html;
      return text.value.trim();
    } else {
      return '';
    }
  }

  /**
   * Get the label text for a field
   * @param {HTMLElement} elt
   * @returns {String}
   */
  getFieldLabel(field) {
    let text = '';
    const parent = field.parentNode;

    if (field.type === 'radio') {
      /** Try to get the value from an element with `data-label-for` attribute matching the field name */
      text = this.getElementText(document.querySelector(`[data-label-for='${field.name}']`));
    } else {
      /** Try to get the value from the label element matching the field id */
      if (text.length === 0 && field.hasAttribute('id')) {
        text = this.getElementText(document.querySelector(`label[for='${field.id}'`));
      }
      /** Try to get the value from the field placeholder */
      if (text.length === 0 && field.hasAttribute('placeholder')) {
        text = field.getAttribute('placeholder');
      }
      /** Try to get the value from the parent element if it's a label or a p */
      if (text.length === 0 && (parent.tagName.toLowerCase() === 'label' || parent.tagName.toLowerCase() === 'p')) {
        text = this.getElementText(field.parentNode);
      }
    }
    /** Try to get the value from the parent element's first p or span element */
    if (text.length === 0) {
      /** Go one level upper if the parent is a label */
      const ancestor = field.parentNode.tagName.toLowerCase() === 'label' ? parent.parentNode : parent;
      const textNode = ancestor.querySelector('p:first-of-type') || ancestor.querySelector('span:first-of-type');
      text = this.getElementText(textNode);
    }
    /** Get the value from element name */
    if (text.length === 0) {
      text = field.name;
    }
    return text.endsWith(':') ? text : `${text}:`;
  }

  /**
   * Validate inputs by type of data
   * @param {HTMLElement} elt
   * @returns {Boolean}
   */
  validateInputsByType(elt) {
    const val = elt.value.trim();
    switch (elt.getAttribute('type')) {
      case 'email':
        return val.match(/^\w[\w\-.]*@\w[\w\-.]*\.[a-z]{2,4}$/i);
      case 'url':
        return val.match(/(http|ftp|https):\/\/[\w\-_]+(\.[\w\-_]+)+([\w\-.,@?^=%&amp;:/~+#]*[\w\-@?^=%&amp;/~+#])?/i);
      case 'tel':
        return val
          .replace(/\./g, '')
          .replace(/\//g, '')
          .replace(/\s+/g, '')
          .match(/^\+?[0-9]{6,}$/);
      case 'number':
        return this.isValidNumber(val.replace(/,/, '.'), this.isInt(elt)) || !this.isInRange(elt);
      case 'file':
        if (elt.hasAttribute('data-maxsize')) {
          if (elt.files.length > 0) {
            const file = elt.files.item(0);
            const maxSize = parseInt(elt.getAttribute('data-maxsize'), 10);
            // console.info(file.size, maxSize);
            if (file.size > maxSize) return false;
          }
        }
      // no-break;
      // eslint-disable-next-line no-fallthrough
      default:
        let minLength = 0;
        if (elt.hasAttribute('minlength')) {
          minLength = parseInt(elt.getAttribute('minlength'), 10) - 1;
        }
        if (val.length <= minLength) {
          return false;
        }
        if (elt.hasAttribute('maxlength')) {
          return val.length <= parseInt(elt.getAttribute('maxlength'), 10);
        }
        return true;
    }
  }

  /**
   * Check if number input is set to ints or floats
   * @param {HTMLElement} elt
   * @returns {Boolean}
   */
  isInt(elt) {
    return elt.getAttribute('step') !== null && elt.getAttribute('step').indexOf('.') > -1 ? false : true;
  }

  /**
   * Check if the number is valid
   * @param {String} number Number to check
   * @param {Boolean} intsOnly Integer or float
   * @returns {Boolean}
   */
  isValidNumber(number, intsOnly) {
    number = number.replace(/^\s+|\s+$/g, '');
    number = number.replace(/(<([^>]+)>)/gi, '');
    const val = intsOnly ? parseInt(number, 10) : parseFloat(number);
    return String(val) === number;
  }

  /**
   * Check if number input is in the given range
   * @param {HTMLElement} elt
   */
  isInRange(elt) {
    if (elt.getAttribute('min') !== undefined) {
      if (parseFloat(elt.value) < parseFloat(elt.getAttribute('min'))) {
        if (elt.value.trim().length > 0) return false;
      }
    }
    if (elt.getAttribute('max') !== undefined) {
      if (parseFloat(elt.value) > parseFloat(elt.getAttribute('max'))) {
        if (elt.value.trim().length > 0) return false;
      }
    }
    return true;
  }

  /**
   * Set valid class name
   * @param {HTMLElement} elt
   */
  setValid(elt) {
    elt.classList.remove('is-invalid');
    elt.classList.add('is-valid');
  }

  /**
   * Set invalid class name
   * @param {HTMLElement} elt
   */
  setInvalid(elt) {
    elt.classList.remove('is-valid');
    elt.classList.add('is-invalid');
  }

  /**
   * Remove valid & invalid class name
   * @param {HTMLElement} elt
   */
  setDefault(elt) {
    elt.classList.remove('is-valid');
    elt.classList.remove('is-invalid');
  }
}



// WEBPACK FOOTER //
// ./scripts/util/FormValidator.js